/**
 * Token bridge — Bearer-token fallback for Safari iOS (Jun 2026).
 *
 * Why this module exists
 * ----------------------
 * Production user رندا on Safari iPhone saw a blank dashboard right
 * after logging in. Root cause: Safari ITP blocks cross-site cookies
 * by default (since 13.1). When the frontend (mespplatform.com) talks
 * to a backend on a different origin, the `Set-Cookie` header sent on
 * `/api/auth/login` is silently discarded. Login appears successful
 * (the response body has user data) but the very next `/api/auth/me`
 * call has no cookie → 401 → AuthContext clears → blank screen.
 *
 * The backend already accepts BOTH the `access_token` cookie AND an
 * `Authorization: Bearer <token>` header (see `auth_utils.py::
 * get_current_user`). We exploit that by:
 *   1. Storing the access + refresh tokens in localStorage on every
 *      identity-issuing endpoint (login, refresh, 2FA verify,
 *      registration apply, legacy-registration apply).
 *   2. Attaching a global axios request interceptor that adds the
 *      Authorization header on every API call when a token exists.
 *   3. Attaching a global axios response interceptor that, on 401,
 *      tries to refresh and retries the original request once. If
 *      refresh fails, it clears localStorage and lets the original
 *      401 bubble up so the AuthContext can drop the user.
 *
 * Storage choice: localStorage (not sessionStorage / IndexedDB).
 *   - sessionStorage drops on tab close — defeats persistent login.
 *   - IndexedDB is overkill for two short opaque strings.
 *   - localStorage IS protected by our existing CSP (`script-src 'self'
 *     'unsafe-inline'`) so XSS surface is the same as before. Adding
 *     localStorage doesn't widen exposure beyond what cookies already
 *     suffer if XSS landed.
 *
 * Cookie compatibility
 *   We DO NOT remove the cookies. Chrome / desktop browsers still get
 *   them and continue to use them. This module is purely additive.
 */
const ACCESS_KEY = 'mesp_access_token';
const REFRESH_KEY = 'mesp_refresh_token';

// Safe wrappers — Safari Private Browsing throws on `localStorage.setItem`
// when the quota is exhausted. Catching the throw keeps the rest of the
// app running (cookies still work for users who aren't blocked by ITP).
const safeGet = (key) => {
  try { return window.localStorage.getItem(key) || null; }
  catch (_e) { return null; }
};
const safeSet = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch (_e) { /* private-browsing quota; cookie path will still try */ }
};

export const getAccessToken = () => safeGet(ACCESS_KEY);
export const getRefreshToken = () => safeGet(REFRESH_KEY);

/**
 * Persist whichever tokens the response body included. Endpoints that
 * don't issue tokens (e.g., /me) simply pass `undefined` for both
 * fields and this function becomes a no-op — we never overwrite a
 * good token with `undefined`.
 */
export const saveTokensFromResponse = (responseData) => {
  if (!responseData || typeof responseData !== 'object') return;
  if (typeof responseData.access_token === 'string' && responseData.access_token) {
    safeSet(ACCESS_KEY, responseData.access_token);
  }
  if (typeof responseData.refresh_token === 'string' && responseData.refresh_token) {
    safeSet(REFRESH_KEY, responseData.refresh_token);
  }
};

export const clearTokens = () => {
  safeSet(ACCESS_KEY, null);
  safeSet(REFRESH_KEY, null);
};

/**
 * Install the request + response interceptors on a given axios instance
 * (we install on the default global axios so every call in the app
 * benefits automatically — no per-call opt-in).
 *
 * The interceptor is idempotent: calling install twice has no effect
 * beyond installing two of each interceptor, which is harmless but
 * wasteful. We guard with a module-level flag.
 */
let installed = false;

// ── Aug 2026 — global session-death handler (prod-health root fix).
// When the session is DEFINITIVELY dead (refresh token rejected, retry
// after refresh still 401, or 401 with no refresh token while a session
// hint exists), every polling component in the app used to keep firing
// 401s forever (347 4xx/day in prod). Instead of patching each poller,
// we clear all session hints and hard-redirect to /login ONCE — the
// page unload kills every timer instantly. Anonymous visitors on
// public pages are never redirected (no session hint = no-op).
const PUBLIC_PATHS = ['/', '/login', '/register', '/welcome', '/auth/callback',
  '/forgot-password', '/reset-password'];
let sessionDeathHandled = false;
const handleSessionDeath = () => {
  if (sessionDeathHandled) return;
  try {
    const hadSession = safeGet('mesp_session_hint') === '1'
      || (typeof document !== 'undefined' && (document.cookie || '').includes('mesp_session_hint=1'))
      || !!getAccessToken() || !!getRefreshToken();
    if (!hadSession) return;
    sessionDeathHandled = true;
    clearTokens();
    try { localStorage.removeItem('mesp_session_hint'); } catch (_e) { /* ignore */ }
    try { localStorage.removeItem('mesp_user_snapshot'); } catch (_e) { /* ignore */ }
    try { document.cookie = 'mesp_session_hint=; Max-Age=0; path=/'; } catch (_e) { /* ignore */ }
    const path = (typeof window !== 'undefined' && window.location?.pathname) || '/';
    const isPublic = PUBLIC_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
    if (!isPublic) {
      window.location.assign('/login?expired=1');
    }
  } catch (_e) { /* never break the original error flow */ }
};

export const installAxiosBearerBridge = (axiosInstance, apiBaseUrl) => {
  if (installed) return;
  installed = true;

  // ── Request interceptor: attach Authorization on API calls only.
  axiosInstance.interceptors.request.use((config) => {
    // Only touch requests that hit OUR backend. Third-party axios calls
    // (e.g., libphonenumber-js doesn't make any, but defensively…) stay
    // untouched. We match by URL prefix because relative requests get
    // resolved against `window.location` not against `apiBaseUrl`.
    const url = (config.url || '').toString();
    const isOurApi = url.startsWith(apiBaseUrl) || url.startsWith('/api/');
    if (!isOurApi) return config;
    // Jun 2026 (prod-health) — once the session is DEFINITIVELY dead and
    // the redirect to /login is in flight, cancel any further API calls
    // client-side (pollers racing the page unload were still producing
    // 401 bursts in prod). Auth endpoints stay allowed so the login page
    // works if the redirect is somehow interrupted.
    if (sessionDeathHandled && !url.includes('/api/auth/')) {
      const cancelErr = new Error('session-expired');
      cancelErr.code = 'ERR_CANCELED';
      cancelErr.__CANCEL__ = true; // axios.isCancel(err) === true
      return Promise.reject(cancelErr);
    }
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      // Only set if not already set (allow per-call override, e.g.,
      // for an admin impersonation call that wants no auth header).
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  // ── Response interceptor: one-shot refresh on 401.
  // Refresh-loop guard: a 401 from the refresh endpoint itself must
  // NEVER trigger another refresh attempt (would loop forever). We
  // mark the original request with `_bearerRetry = true` after the
  // first refresh attempt so we don't retry twice.
  let refreshInFlight = null; // Promise<string|null> — shared if multiple 401s race
  axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;
      // Aug 2026 — Atlas failover resilience: during brief replica-set
      // elections (~10-15s) the backend returns fast 503 + Retry-After.
      // Transparently retry idempotent GETs (max 2, honouring Retry-After)
      // so users never notice the blip. Streams/uploads excluded.
      if (status === 503 && original
          && (original.method || 'get').toLowerCase() === 'get'
          && !(original.url || '').includes('/stream')
          && (original._transient503 || 0) < 2) {
        original._transient503 = (original._transient503 || 0) + 1;
        const ra = parseFloat(error.response?.headers?.['retry-after']) || 2;
        await new Promise((r) => setTimeout(r, Math.min(ra, 5) * 1000));
        return axiosInstance.request(original);
      }
      if (status !== 401 || !original) throw error;
      const url = (original.url || '').toString();
      if (url.includes('/api/auth/refresh') || url.includes('/api/auth/login')) {
        // Refresh failed OR login failed — let the original 401 propagate.
        clearTokens();
        if (url.includes('/api/auth/refresh')) handleSessionDeath();
        throw error;
      }
      if (original._bearerRetry) {
        // We already retried once; give up to avoid infinite loops.
        clearTokens();
        handleSessionDeath();
        throw error;
      }
      const refresh = getRefreshToken();
      if (!refresh) {
        // No refresh token to use; nothing we can do.
        handleSessionDeath();
        throw error;
      }
      // Single-flight: if another 401 raced here, await the same refresh.
      if (!refreshInFlight) {
        refreshInFlight = axiosInstance.post(
          `${apiBaseUrl}/api/auth/refresh`,
          { refresh_token: refresh },
          { withCredentials: true },
        ).then((r) => {
          saveTokensFromResponse(r.data);
          return r.data?.access_token || null;
        }).catch((refreshErr) => {
          // Only a REAL rejection (401/403) means the refresh token is
          // dead — clear it. A network drop or 5xx during a deployment
          // is transient: keep the tokens so the session survives the
          // restart and the next request can refresh successfully.
          const rs = refreshErr?.response?.status;
          if (rs === 401 || rs === 403) clearTokens();
          return null;
        }).finally(() => { refreshInFlight = null; });
      }
      const newAccess = await refreshInFlight;
      if (!newAccess) {
        handleSessionDeath();
        throw error;
      }
      // Retry the original request with the new token.
      original._bearerRetry = true;
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccess}`;
      return axiosInstance.request(original);
    },
  );
};
